// Admin-only "Portal Modules" toggle panel — sits on the franchisee
// detail page and lets admin decide which sections of the franchisee's
// own portal they get to see.
//
// Defaults applied server-side: Map / Calendar / Files = ON,
// Invoicing = OFF. The server persists changes via
// PATCH /api/franchisees/:id/portal-modules.
//
// Feb-2026 admin display fix (+HUB entitlement mirror):
// The RAW toggle values in `franchisee.portal_modules` are always the
// admin-controlled master switches (kept as-is on save/reload). But
// for the four PAID +HUB rows we additionally show the effective
// subscription state derived server-side into
// `franchisee.hub_plus_effective_state` +
// `franchisee.hub_plus_entitled_modules` — so if a franchisee's
// subscription is expired or cancelled while their toggle is still
// ON, the row reads "INACTIVE — SUBSCRIPTION EXPIRED / CANCELLED"
// instead of the misleading "Enabled". Stored toggle values are NEVER
// touched here — reactivation later restores the previous UI state
// automatically.
//
// Feb-2026 (part 15): per-module Temporary Access override — HQ can
// flip one paid module on for a franchisee without changing their
// subscription. Persisted in `franchisee.temporary_module_overrides`
// via PATCH /api/franchisees/:id/temporary-module-override. Rendered
// as a secondary amber pill under the main status pill on paid rows.
//
// Why a custom on/off pill rather than a Shadcn Switch? Visual match
// with the rest of the franchisee detail page (uppercase pill style)
// + we get a focus ring + we don't add another component dependency.
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Map as MapIcon, Calendar, Folder, FileText, Loader2, Sparkles, Megaphone, ShoppingBag } from "lucide-react";

const STANDARD_MODULES = [
  { key: "map",       label: "Territory Map", icon: MapIcon,  description: "View their assigned postcode sectors and homes count." },
  { key: "calendar",  label: "Calendar",      icon: Calendar, description: "Events, workshops, and training sessions." },
  { key: "files",     label: "File Vault",    icon: Folder,   description: "Brand assets, marketing materials, ops docs." },
];

// Plus add-ons — subscription-gated extras that unlock paid features.
const PLUS_MODULES = [
  { key: "territory_plus", label: "My Territory+", icon: MapIcon,   description: "Upgrades Territory page — plot contacts, marketing leads, area analytics. Adds the “+” suffix to the menu item." },
  { key: "marketing",      label: "Marketing+",     icon: Megaphone, description: "Social-post templates, local marketing kits, campaign tools." },
  { key: "invoicing",      label: "Invoicing+",     icon: FileText,  description: "Personal invoicing tool with client manager and Xero export." },
  { key: "bookings",       label: "Bookings+",      icon: Calendar,  description: "Client-booking manager — recurring sessions, attendance and franchisee diary." },
  { key: "shape_orders",   label: "Franchise Store", icon: ShoppingBag, description: "Order shape sets, signage and branded clothing straight from HQ — replaces the WooCommerce shop checkout." },
];

// Paid +HUB module keys — Franchise Store is a Plus-panel row but is
// NOT a paid +HUB module, so it never gets an entitlement override.
const PAID_HUB_KEYS = new Set(["territory_plus", "marketing", "invoicing", "bookings"]);

// Given the server-derived effective state + entitled set + the raw
// stored toggle, decide how the row's status pill should render for a
// paid +HUB module. Returns { on, disabled, label, tone } where tone
// drives the pill colour and `on`/`disabled` are only used to keep
// keyboard/aria semantics consistent with the standard rows.
function deriveHubPlusRowDisplay({ key, storedOn, effectiveState, entitledSet }) {
  // Not a paid row, or franchisee never enrolled → behave as today.
  if (!PAID_HUB_KEYS.has(key) || !effectiveState || effectiveState === "no_scheme") {
    return { override: false };
  }
  // Toggle OFF — no override needed, same "Disabled" pill as today.
  if (!storedOn) {
    return { override: false };
  }
  // Toggle ON AND currently entitled → normal "Enabled" pill.
  if (entitledSet.has(key)) {
    return { override: false };
  }
  // Toggle ON but NOT entitled → surface the subscription state on the
  // row. cancelled_pending_active would be entitled above so anything
  // remaining here is a genuinely non-active state.
  switch (effectiveState) {
    case "expired":
      return { override: true, label: "Inactive — Subscription Expired", tone: "amber" };
    case "cancelled":
      return { override: true, label: "Inactive — Subscription Cancelled", tone: "rose" };
    case "pending":
      return { override: true, label: "Inactive — Pending Activation", tone: "stone" };
    case "payment_issue":
      return { override: true, label: "Inactive — Payment Issue", tone: "amber" };
    case "active":
      // Subscription is active but the franchisee didn't select this
      // specific paid module — flag it so the admin knows the row is
      // toggled on but the subscription doesn't cover it.
      return { override: true, label: "Inactive — Not in Subscription", tone: "stone" };
    default:
      return { override: true, label: "Inactive — Subscription Inactive", tone: "stone" };
  }
}

const OVERRIDE_TONE_CLASSES = {
  amber: "bg-amber-100 text-amber-900 border border-amber-300",
  rose:  "bg-rose-100 text-rose-900 border border-rose-300",
  stone: "bg-stone-200 text-stone-800 border border-stone-300",
};

export default function PortalModulesPanel({ franchisee, onChanged }) {
  // Local mirror of the toggles — we optimistically update on click and
  // roll back on API failure so toggling feels instant.
  const initial = franchisee.portal_modules || {};
  const [state, setState] = useState({
    map:            initial.map            !== false, // default ON
    calendar:       initial.calendar       !== false, // default ON
    files:          initial.files          !== false, // default ON
    territory_plus: initial.territory_plus === true,  // default OFF (plus)
    marketing:      initial.marketing      === true,  // default OFF (plus)
    invoicing:      initial.invoicing      === true,  // default OFF (plus)
    bookings:       initial.bookings       === true,  // default OFF (plus)
    shape_orders:   initial.shape_orders   === true,  // default OFF (plus)
  });
  const [savingKey, setSavingKey] = useState(null);
  // Per-module Temporary Access overrides — stored server-side under
  // `franchisee.temporary_module_overrides.<key> = { enabled: true,
  // changed_by, changed_at }`. Local mirror uses the raw `enabled`
  // flag so `!!temp[key]` is enough to drive the pill.
  const initialTemp = franchisee.temporary_module_overrides || {};
  const [temp, setTemp] = useState({
    territory_plus: !!(initialTemp.territory_plus && initialTemp.territory_plus.enabled),
    marketing:      !!(initialTemp.marketing      && initialTemp.marketing.enabled),
    invoicing:      !!(initialTemp.invoicing      && initialTemp.invoicing.enabled),
    bookings:       !!(initialTemp.bookings       && initialTemp.bookings.enabled),
  });
  const [savingTempKey, setSavingTempKey] = useState(null);

  const allModules = [...STANDARD_MODULES, ...PLUS_MODULES];

  // Server-derived +HUB block. Absent for legacy franchisees — the
  // derive helper treats a missing/empty state as "no override".
  const effectiveState = franchisee.hub_plus_effective_state || "no_scheme";
  const entitledSet = new Set(franchisee.hub_plus_entitled_modules || []);

  const toggle = async (key) => {
    const next = { ...state, [key]: !state[key] };
    setState(next);  // optimistic
    setSavingKey(key);
    try {
      await api.patch(`/franchisees/${franchisee.id}/portal-modules`, { [key]: next[key] });
      toast.success(`${allModules.find((m) => m.key === key).label} ${next[key] ? "enabled" : "disabled"}`);
      onChanged?.();
    } catch (e) {
      setState(state);  // rollback
      toast.error("Couldn't save toggle — try again");
      console.error("[PortalModules] toggle failed", e);
    } finally {
      setSavingKey(null);
    }
  };

  const toggleTemp = async (key) => {
    const nextValue = !temp[key];
    setTemp((prev) => ({ ...prev, [key]: nextValue }));  // optimistic
    setSavingTempKey(key);
    try {
      await api.patch(
        `/franchisees/${franchisee.id}/temporary-module-override`,
        { module: key, enabled: nextValue },
      );
      const label = allModules.find((m) => m.key === key)?.label || key;
      toast.success(
        nextValue
          ? `${label}: temporary access enabled`
          : `${label}: temporary access removed`,
      );
      onChanged?.();
    } catch (e) {
      setTemp((prev) => ({ ...prev, [key]: !nextValue }));  // rollback
      toast.error("Couldn't save temporary access — try again");
      console.error("[PortalModules] temp toggle failed", e);
    } finally {
      setSavingTempKey(null);
    }
  };

  const renderRow = (m) => {
    const Icon = m.icon;
    const on = !!state[m.key];
    const saving = savingKey === m.key;
    const override = deriveHubPlusRowDisplay({
      key: m.key,
      storedOn: on,
      effectiveState,
      entitledSet,
    });
    const isPaidHub = PAID_HUB_KEYS.has(m.key);
    const tempOn = isPaidHub && !!temp[m.key];
    const savingTemp = savingTempKey === m.key;
    return (
      <div key={m.key} className="flex items-center justify-between p-3 bg-stone-50 border border-stone-200 rounded-lg gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`p-1.5 rounded ${on || tempOn ? "bg-stone-950 text-[#dddd16]" : "bg-stone-200 text-stone-500"}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-stone-950">{m.label}</div>
            <div className="text-[11px] text-stone-500 leading-snug">{m.description}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <button
            type="button"
            onClick={() => toggle(m.key)}
            disabled={saving}
            data-testid={`portal-module-toggle-${m.key}`}
            aria-pressed={on}
            className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-md transition-colors shrink-0 inline-flex items-center gap-1.5 ${
              override.override
                ? OVERRIDE_TONE_CLASSES[override.tone] || OVERRIDE_TONE_CLASSES.stone
                : on
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-stone-300 hover:bg-stone-400 text-stone-800"
            } disabled:opacity-50 disabled:cursor-wait`}>
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            {override.override ? override.label : on ? "Enabled" : "Disabled"}
          </button>
          {isPaidHub && (
            <button
              type="button"
              onClick={() => toggleTemp(m.key)}
              disabled={savingTemp}
              data-testid={`portal-module-temp-override-${m.key}`}
              aria-pressed={tempOn}
              title={tempOn
                ? "Granted manually by Head Office — no billing attached."
                : "Grant this franchisee temporary access to this module without changing their subscription."}
              className={`px-2 py-1 text-[9px] font-bold uppercase tracking-widest rounded-md inline-flex items-center gap-1.5 ${
                tempOn
                  ? "bg-amber-500 hover:bg-amber-600 text-stone-950 border border-amber-600"
                  : "bg-white hover:bg-stone-100 text-stone-600 border border-stone-300"
              } disabled:opacity-50 disabled:cursor-wait`}>
              {savingTemp && <Loader2 className="w-3 h-3 animate-spin" />}
              {tempOn ? "Temporary access — Enabled" : "Temporary access"}
            </button>
          )}
          {isPaidHub && tempOn && (
            <div
              className="text-[10px] text-stone-500 italic max-w-[220px] text-right"
              data-testid={`portal-module-temp-override-note-${m.key}`}
            >
              Granted manually by Head Office — no billing attached.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="portal-modules-panel">
      <p className="text-xs text-stone-600">
        Choose which portal sections this franchisee can see. Toggles apply instantly the next time they sign in or refresh.
      </p>

      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mt-3 mb-1">Standard modules</div>
      <div className="space-y-2">{STANDARD_MODULES.map(renderRow)}</div>

      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mt-5 mb-1 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> Plus add-ons <span className="text-stone-400 normal-case tracking-normal">· paid subscription</span>
      </div>
      <div className="space-y-2">{PLUS_MODULES.map(renderRow)}</div>
    </div>
  );
}
